import { useEffect, useRef } from "react";

const SLOT_HEIGHT_5_MIN = 45;
const SLOT_HEIGHT_DEFAULT = 60;
const ROW_GAP = 12;
const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Brings the board to roughly now when it is first shown for a day.
 *
 * Deliberately not on every render of `timeSlots`. The query rebuilds that array on every
 * fetch, so the effect used to re-run whenever anything refetched — including after a
 * status change — and yanked the board back to the current hour while the user was
 * looking somewhere else. The scroll belongs to the day being shown, not to the identity
 * of an array.
 */
export const useAutoScrollToCurrentTime = (
  ref,
  { timeSlots, slotDurationMinutes }
) => {
  const scrolledFor = useRef(null);

  useEffect(() => {
    if (!timeSlots?.length || !ref.current) return;

    // What the scroll actually depends on: which day is on screen and how tall its rows
    // are. A refetch that returns the same day leaves this unchanged, so the board stays
    // where the user put it.
    const day = `${timeSlots[0].start}-${timeSlots.length}-${slotDurationMinutes}`;

    if (scrolledFor.current === day) return;

    scrolledFor.current = day;

    const oneHourAgo = new Date(Date.now() - ONE_HOUR_MS);
    const targetIndex = timeSlots.findIndex((slot) => slot.end > oneHourAgo);

    if (targetIndex <= 0) return;

    const slotHeight =
      slotDurationMinutes === 5 ? SLOT_HEIGHT_5_MIN : SLOT_HEIGHT_DEFAULT;

    ref.current.scrollTop = targetIndex * (slotHeight + ROW_GAP);
  }, [timeSlots, slotDurationMinutes, ref]);
};
